import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import type { WapTaskType } from "@whatslly/types/WapTask";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { FieldStore } from "@whatslly/widget/stores/FieldStore";
import { backgroundClient } from "@whatslly/commons/background/client";
import { getConfigFromLoginProvider } from "@whatslly/commons/login-provider/getConfigFromLoginProvider";
import { BgCommands } from "@whatslly/commons/bgCommands";
import type { GetWapNotificationsResponseType } from "@whatslly/types/GetWapNotificationsResponse";
@injectable()
export class NotificationsStore {
  private _overDueOnly = true;
  wapTasks?: WapTaskType[];
  isInitialized = false;
  loadingNotifications = false;
  constructor(@inject(FieldStore) private fieldStore: FieldStore) {
    makeObservable(this, {
      wapTasks: observable,
      isInitialized: observable,
      _overDueOnly: observable,
      loadingNotifications: observable,
      overDueOnly: computed,
      loadNotifications: action,
      updateWapTasks: action,
      forceReloadNotifications: action,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }

  updateWapTasks(newWapTasks: WapTaskType[]) {
    this.wapTasks = newWapTasks;
  }

  get overDueOnly() {
    return this._overDueOnly;
  }
  set overDueOnly(newState: boolean) {
    this._overDueOnly = newState;
  }

  async forceReloadNotifications({
    showWhatsllyTaskIcon,
    loginProvider,
  }: {
    showWhatsllyTaskIcon: boolean | undefined;
    loginProvider: LoginProviderType | undefined;
  }) {
    if (!showWhatsllyTaskIcon) {
      return;
    }
    this.loadingNotifications = true;
    this.isInitialized = true;
    const { urlSuffix } = getConfigFromLoginProvider(loginProvider, `notifications`);
    const res = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetWapNotificationsResponseType>(BgCommands.HTTP_REQUEST, {
      urlSuffix,
      method: "POST",
      body: JSON.stringify({ overDueOnly: this._overDueOnly }),
    });
    if (!res || !res.success) {
      this.loadingNotifications = false;
      return;
    }
    runInAction(() => {
      this.wapTasks = res.wapTasks;
      this.loadingNotifications = false;
    });
  }

  reset() {
    this.isInitialized = false;
  }

  async loadNotifications({
    showWhatsllyTaskIcon,
    loginProvider,
    defaultNotificationsOverDueOnly,
  }: {
    showWhatsllyTaskIcon: boolean | undefined;
    loginProvider: LoginProviderType | undefined;
    defaultNotificationsOverDueOnly?: boolean;
  }) {
    if (this.isInitialized) {
      return;
    }
    if (defaultNotificationsOverDueOnly !== undefined) {
      this._overDueOnly = defaultNotificationsOverDueOnly;
    }
    await this.forceReloadNotifications({ showWhatsllyTaskIcon, loginProvider });
  }
}
